/**
 * TelegramBridge — IPlatformBridge implementation for Telegram WebApp
 */

import type { IPlatformBridge, HapticStyle, HapticNotificationType } from './types';
import type { PlatformType, PlatformUserDTO, PlatformThemeParamsDTO } from '@tg-miniapp/shared-types';

let isDispatcherRegistered = false;
const backButtonCallbacks: Array<() => void> = [];

function backButtonDispatcher() {
  const lastCallback = backButtonCallbacks[backButtonCallbacks.length - 1];
  if (lastCallback) {
    lastCallback();
  }
}

export class TelegramBridge implements IPlatformBridge {
  readonly platform: PlatformType = 'telegram';

  private get tg(): any {
    return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  }

  get isAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
  }

  get initData(): string {
    let resolved = this.tg?.initData || '';

    if (!resolved && typeof window !== 'undefined') {
      try {
        const rawHash = window.location.hash.slice(1);
        const hashParams = new URLSearchParams(rawHash);
        const tgWebAppData = hashParams.get('tgWebAppData');
        if (tgWebAppData) {
          resolved = tgWebAppData;
        } else if (rawHash.startsWith('tgWebAppData=')) {
          const ampersandIdx = rawHash.indexOf('&');
          resolved = ampersandIdx !== -1 
            ? rawHash.slice('tgWebAppData='.length, ampersandIdx) 
            : rawHash.slice('tgWebAppData='.length);
          resolved = decodeURIComponent(resolved);
        }

        if (!resolved) {
          const searchParams = new URLSearchParams(window.location.search);
          const searchData = searchParams.get('tgWebAppData');
          if (searchData) resolved = searchData;
        }
      } catch {}
    }

    if (resolved) {
      try {
        sessionStorage.setItem('tg_init_data_backup', resolved);
      } catch {}
      return resolved;
    }

    try {
      const cachedBackup = sessionStorage.getItem('tg_init_data_backup');
      if (cachedBackup) return cachedBackup;
    } catch {}

    return '';
  }

  get user(): PlatformUserDTO | null {
    const rawUser = this.tg?.initDataUnsafe?.user;
    if (rawUser) {
      return {
        id: `tg:${rawUser.id}`,
        rawId: rawUser.id,
        platform: 'telegram',
        firstName: rawUser.first_name || 'Пользователь',
        lastName: rawUser.last_name,
        username: rawUser.username,
        languageCode: rawUser.language_code,
        isPremium: rawUser.is_premium
      };
    }
    return null;
  }

  get startParam(): string | undefined {
    if (this.tg?.initDataUnsafe?.start_param) {
      return this.tg.initDataUnsafe.start_param;
    }
    if (typeof window !== 'undefined') {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const fromSearch = searchParams.get('tgWebAppStartParam') || searchParams.get('startapp') || searchParams.get('start');
        if (fromSearch) return fromSearch;

        const rawHash = window.location.hash.slice(1);
        const hashParams = new URLSearchParams(rawHash);
        const fromHash = hashParams.get('tgWebAppStartParam') || hashParams.get('startapp') || hashParams.get('start');
        if (fromHash) return fromHash;

        const tgWebAppData = hashParams.get('tgWebAppData') || searchParams.get('tgWebAppData');
        if (tgWebAppData) {
          const decoded = decodeURIComponent(tgWebAppData);
          const dataParams = new URLSearchParams(decoded);
          const fromData = dataParams.get('start_param') || dataParams.get('startapp') || dataParams.get('start');
          if (fromData) return fromData;
        }
      } catch {}
    }
    return undefined;
  }

  get colorScheme(): 'light' | 'dark' {
    return this.tg?.colorScheme === 'dark' ? 'dark' : 'light';
  }

  get themeParams(): PlatformThemeParamsDTO {
    const tp = this.tg?.themeParams;
    return {
      bgColor: tp?.bg_color,
      textColor: tp?.text_color,
      hintColor: tp?.hint_color,
      linkColor: tp?.link_color,
      buttonColor: tp?.button_color,
      buttonTextColor: tp?.button_text_color,
      secondaryBgColor: tp?.secondary_bg_color
    };
  }

  ready(): void {
    try {
      this.tg?.ready();
    } catch {}
  }

  expand(): void {
    try {
      this.tg?.expand();
    } catch {}
  }

  close(): void {
    try {
      this.tg?.close();
    } catch {}
  }

  showBackButton(onClick: () => void): () => void {
    if (!this.tg) return () => {};

    backButtonCallbacks.push(onClick);
    if (!isDispatcherRegistered) {
      this.tg.BackButton?.onClick(backButtonDispatcher);
      isDispatcherRegistered = true;
    }
    this.tg.BackButton?.show();

    return () => {
      const idx = backButtonCallbacks.indexOf(onClick);
      if (idx > -1) {
        backButtonCallbacks.splice(idx, 1);
      }
      if (backButtonCallbacks.length === 0) {
        this.tg?.BackButton?.hide();
        this.tg?.BackButton?.offClick(backButtonDispatcher);
        isDispatcherRegistered = false;
      }
    };
  }

  hapticImpact(style: HapticStyle = 'light'): void {
    try {
      this.tg?.HapticFeedback?.impactOccurred(style);
    } catch {}
  }

  hapticNotification(type: HapticNotificationType): void {
    try {
      this.tg?.HapticFeedback?.notificationOccurred(type);
    } catch {}
  }

  hapticSelection(): void {
    try {
      this.tg?.HapticFeedback?.selectionChanged();
    } catch {}
  }

  openLink(url: string, options?: { tryInstantView?: boolean }): void {
    if (this.tg?.openLink) {
      this.tg.openLink(url, { try_instant_view: options?.tryInstantView });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  shareContent(text: string, url?: string): void {
    const targetUrl = url ? `${url}` : window.location.href;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(targetUrl)}&text=${encodeURIComponent(text)}`;
    this.openLink(shareUrl);
  }

  enableClosingConfirmation(): void {
    try {
      this.tg?.enableClosingConfirmation?.();
    } catch {}
  }

  disableClosingConfirmation(): void {
    try {
      this.tg?.disableClosingConfirmation?.();
    } catch {}
  }
}
